/**
 * Temporary screen-scoped design tokens for **Insights** (`/insights`).
 *
 * Mirrors the insights half of `Reports.dc.html` — the same page-title, tab
 * bar and card-heading chrome the Reports screen draws from, since both pages
 * share one design source (`pageTitle`/`tabStyle` in the script switch on
 * `view === 'insights'`). Duplicated here rather than imported from
 * `screens/Reports` per UI_IMPLEMENTATION_RULES.md § Temporary Screen Tokens —
 * each screen keeps its own copy until everything is re-skinned and the
 * common values get promoted to `src/theme`.
 */

import { contentCard, signer } from "theme/colors";

export const insightsTokens = {
  /** "Insights" title block. */
  header: {
    titleColor: signer.ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    // Darkened from #8A85A0 (3.53:1) — WCAG 1.4.3 needs 4.5:1 for this size.
    subtitleColor: "#746E8C",
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "6px",
  },

  /** Workflow Trends ▸ Progress & Status Distribution. */
  tabs: {
    gap: "34px",
    /**
     * Gap above the tab row. Applied as the *outer* `<Stack>`'s `spacing`
     * prop (a multiplier of the theme's 8px unit — 3.5 × 8 = 28px), not as a
     * plain `mt` on the tab row itself: a `Stack` child's own margin loses to
     * that `Stack`'s built-in sibling-spacing rule, which carries higher CSS
     * specificity regardless of source order.
     */
    marginTopUnits: 3.5,
    marginBottom: "26px",
    borderColor: contentCard.border,
    fontSize: "15px",
    /** Brand accent — drawn as the 2.5px active underline (3.92:1, passes the
     * 3:1 required of a non-text UI indicator by WCAG 1.4.11). */
    activeColor: signer.orange.main,
    /** Same hue, darkened for the active *label*: 15px text needs 4.5:1
     * (WCAG 1.4.3) and the accent itself only reaches 3.92:1. This is
     * `signer.orange.dark` (4.93:1) — the shade the theme already uses wherever
     * the brand orange has to carry text. */
    activeTextColor: signer.orange.dark,
    // Darkened from #8A85A0 (3.53:1) for WCAG 1.4.3.
    inactiveColor: "#746E8C",
    activeWeight: 800,
    inactiveWeight: 600,
    indicatorWidth: "2.5px",
    paddingBottom: "14px",
  },

  /** "Workflow statistics" / "Total document progress" / "Request status
   * distribution" card headings. */
  cardHeading: {
    titleColor: signer.ink,
    titleFontSize: "18px",
    titleFontWeight: 800,
    // Darkened from #8A85A0 (3.53:1) — WCAG 1.4.3 needs 4.5:1 for this size.
    subtitleColor: "#746E8C",
    subtitleFontSize: "13px",
    subtitleMarginTop: "6px",
  },
} as const;

/**
 * Raw media query for the narrow phone column of
 * `Reports - Mobile & Tablet.dc.html`. MUI has no breakpoint between 375 and
 * 390, so the half-step the reference draws between them is expressed as a raw
 * query and applied *after* the `xs` values so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) values, traced from the insights half of
 * `Reports - Mobile & Tablet.dc.html`.
 *
 * Only what differs from the desktop tokens above lives here, so `/insights` at
 * `md` and up is untouched. `sm` / `std` inside `phone` are the reference's 375
 * and 390 columns.
 */
export const insightsResponsiveTokens = {
  phone: {
    paddingX: { sm: "14px", std: "16px" },
    paddingTop: { sm: "14px", std: "16px" },
    paddingBottom: { sm: "32px", std: "34px" },
    gap: "14px",

    subtitleFontSize: { sm: "12px", std: "12.5px" },
    subtitleLineHeight: 1.45,

    tabsGap: { sm: "7px", std: "8px" },

    card: {
      radius: "14px",
      border: contentCard.border,
      padding: { sm: "16px 14px", std: "17px 15px" },
      titleFontSize: { sm: "14px", std: "14.5px" },
      /** Between the card heading and the chart panel under it. */
      panelMarginTop: { sm: "12px", std: "13px" },
      panelRadius: "12px",
      panelPadding: { sm: "14px 8px 10px", std: "15px 9px 10px" },
      /**
       * The reference draws a stylised sparkline with no axes; the real chart
       * carries a date axis and a legend, so it is given the height those need
       * rather than the mock's 130px.
       */
      chartHeight: 220,
    },

    /** Progress ▸ Status cards stack, one rhythm apart. */
    gridGap: "14px",
  },

  tablet: {
    paddingX: "26px",
    // 22px, not 24: the gutter matches Templates at every width — that screen
    // set the house values and Reports/Insights follow it.
    paddingTop: "22px",
    paddingBottom: "40px",
    gap: "16px",

    titleFontSize: "22px",
    titleLetterSpacing: "-.01em",
    subtitleFontSize: "13px",
    subtitleMarginTop: "5px",
    headerGap: "16px",

    tabsGap: "24px",
    tabFontSize: "13.5px",
    tabPaddingBottom: "12px",
    tabPaddingX: "2px",

    card: {
      radius: "16px",
      /** The full-width Workflow Trends card. */
      padding: "22px 22px 24px",
      titleFontSize: "16px",
      /** The two half-width Status cards, which sit a step tighter. */
      statusPadding: "20px 18px 22px",
      statusTitleFontSize: "15px",
      panelMarginTop: "15px",
      panelRadius: "14px",
      panelPadding: "18px 14px 12px",
    },

    /** The reference sets the two status cards side by side at 834. */
    gridGap: "16px",
  },
} as const;

export default insightsTokens;
